"use client";

import React, { useState } from "react";
import Link from "next/link";
import SubmenuLeftBottom from "./SubmenuLeftBottom";
import SubmenuChevron from "./SubmenuChevron";

interface ResponsiveImages {
  desktop?: string;
  ipad?: string;
  mobile?: string;
}

interface ServiceLeftProps {
  submenu?: any[];
  bottomImages?: ResponsiveImages;
}

// One titled column. On mobile it collapses to a tappable row - title,
// description and chevron - that reveals its links; from lg up the links are
// always visible and the column heading links through to its landing page.
// Open state lives in ServiceLeft so only one column is expanded at a time.
const ServiceColumn = ({
  sub,
  isOpen,
  onToggle,
}: {
  sub: any;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const links: any[] =
    sub?.right?.flatMap((group: any) => group?.links ?? []) ?? [];

  const heading = (
    <h4 className="text-subtitle-3 font-bold text-[var(--color-primary-text)]">
      {sub.title}
    </h4>
  );

  return (
    <div className="w-full lg:w-auto lg:flex-1 flex flex-col gap-[16px] lg:gap-[24px] pb-[16px] border-b border-[var(--color-blue-100)] lg:pb-0 lg:border-0">
      <div
        className="top-header flex items-center justify-between gap-[12px] lg:block cursor-pointer lg:cursor-auto"
        onClick={onToggle}
      >
        <div className="flex flex-col gap-[8px] lg:gap-[12px]">
          {/* The mobile row toggles rather than navigates, so the title is
              plain text there and a link from lg up. */}
          <span className="lg:hidden">{heading}</span>
          <Link href={sub.link || "#!"} className="hidden lg:block">
            {heading}
          </Link>
          {sub.description && (
            <p className="text-subtitle-4 font-semibold text-[var(--color-secondary-text)] opacity-[70%]">
              {sub.description}
            </p>
          )}
        </div>
        {links.length > 0 && (
          <SubmenuChevron
            isOpen={isOpen}
            className="lg:hidden text-[var(--color-primary-text)]"
          />
        )}
      </div>
      <div className={`bottom-header ${isOpen ? "block" : "hidden"} lg:block`}>
        <ul className="flex flex-col gap-[16px]">
          {links.map((link: any) => (
            <li key={link.id}>
              <Link
                href={link.href || "#!"}
                target={link.isExternal ? link.target || "_blank" : undefined}
                rel={link.isExternal ? "noopener noreferrer" : undefined}
                className="pl-[12px] block  border-l-2 border-[#B0D9F780] leading-[150%] text-subtitle-4 font-[400] text-[var(--color-primary-text)] transition-all duration-300 ease-in-out hover:text-[var(--color-text-button)] hover:border-[var(--color-text-button)]"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const ServiceLeft: React.FC<ServiceLeftProps> = ({
  submenu = [],
  bottomImages,
}) => {
  const [openId, setOpenId] = useState<number | null>(null);

  return (
    <>
      <div className="flex-1 w-full min-w-0 flex flex-col gap-2 justify-between">
        <div className="flex flex-wrap gap-[16px] lg:gap-[24px_15px] xl:gap-[24px] w-full min-[1280px]:max-[1366px]:!gap-[15px]">
          {submenu.map((sub) => (
            <ServiceColumn
              key={sub.id}
              sub={sub}
              isOpen={openId === sub.id}
              onToggle={() =>
                setOpenId((current) => (current === sub.id ? null : sub.id))
              }
            />
          ))}
        </div>
        <SubmenuLeftBottom images={bottomImages} />
      </div>
    </>
  );
};

export default ServiceLeft;
